import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ClipboardList } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useQuote } from "@/context/QuoteContext";
import { InquiryForm } from "@/components/InquiryForm";

export const QuoteDrawer = () => {
  const { items, open, setOpen, setQty, remove, clear } = useQuote();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="quote-drawer">
        <SheetHeader className="text-left">
          <p className="sr-label">Wholesale quote request</p>
          <SheetTitle className="font-display text-2xl font-extrabold uppercase text-[#0B1F4B]">Your quote list</SheetTitle>
          <SheetDescription>Quantities are in cases. Pricing, availability and case-pack details are confirmed at quotation.</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="mt-10 text-center text-slate-500" data-testid="quote-empty-state">
            <ClipboardList className="mx-auto mb-3 text-[#1E7CF2]" size={36} />
            <p className="text-sm">No products yet. Browse the catalog and add products to request a quote.</p>
            <Link to="/catalog" onClick={() => setOpen(false)} className="sr-btn-primary mt-5" data-testid="quote-browse-catalog">Browse catalog</Link>
          </div>
        ) : (
          <>
            <ul className="mt-6 divide-y divide-slate-100" data-testid="quote-items-list">
              {items.map((i) => (
                <li key={i.product_id} className="py-3 flex items-center gap-3" data-testid={`quote-item-${i.product_id}`}>
                  <img src={i.image} alt="" className="w-14 h-14 rounded-lg object-cover bg-slate-100" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{i.brand_name}</p>
                    <p className="text-sm font-bold text-[#0B1F4B] truncate">{i.name}</p>
                    <p className="text-xs text-slate-500">{i.size}{i.case_pack ? ` · ${i.case_pack}/case` : ""}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setQty(i.product_id, i.quantity - 1)} className="w-7 h-7 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-100" data-testid={`quote-qty-minus-${i.product_id}`}><Minus size={12} /></button>
                    <input type="number" min={1} value={i.quantity} onChange={(e) => setQty(i.product_id, parseInt(e.target.value) || 1)} className="w-12 text-center text-sm font-mono border border-slate-300 rounded-md py-1" data-testid={`quote-qty-input-${i.product_id}`} />
                    <button onClick={() => setQty(i.product_id, i.quantity + 1)} className="w-7 h-7 rounded-full border border-slate-300 flex items-center justify-center hover:bg-slate-100" data-testid={`quote-qty-plus-${i.product_id}`}><Plus size={12} /></button>
                  </div>
                  <button onClick={() => remove(i.product_id)} className="text-slate-400 hover:text-[#E63946]" data-testid={`quote-remove-${i.product_id}`}><Trash2 size={16} /></button>
                </li>
              ))}
            </ul>
            <div className="flex justify-between items-center text-xs text-slate-500 mt-2">
              <span data-testid="quote-total-cases">{items.reduce((s, i) => s + i.quantity, 0)} cases · {items.length} SKUs</span>
              <button onClick={clear} className="underline hover:text-[#E63946]" data-testid="quote-clear-button">Clear list</button>
            </div>
            <div className="mt-6 pt-6 border-t border-slate-200">
              <p className="sr-label mb-3">Your details</p>
              <InquiryForm type="quote" items={items} testPrefix="quote" submitLabel="Submit quote request"
                onSuccess={() => { clear(); setOpen(false); }} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
